import { FC, memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { UserWithSubscriptions } from '@api/apiCompat';
import { useUser } from '@hooks/api/useUser';
import { SettingsProvider } from '@pages/Settings/context';
import styled from 'styled-components';

import { Alert, Box, Typography } from '@mui/material';
import Loading from '@components/Loading/Loading';

import ActiveTab from './components/ActiveTab';
import ToggleTabs from './components/ToggleTabs';
import { Indicators } from './enums';

// Content wrapper for Settings page
const SettingsContent = styled.div`
  padding: var(--sk-space-6);
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: var(--sk-space-4);
  }
`;

const SettingsHeader = styled.h1`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-2xl);
  font-weight: 600;
  color: var(--sk-text-primary);
  margin-bottom: var(--sk-space-4);
`;

const Settings: FC = () => {
  const { t } = useTranslation();
  const { data: userData, isLoading } = useUser();
  const [searchParams] = useSearchParams();

  // Cast to extended type with subscriptions
  const me = userData as UserWithSubscriptions | undefined;

  const currentTab = searchParams.get('tab') || Indicators.PROFILE;

  // Dynamic page title based on current tab
  const getPageTitle = () => {
    switch (currentTab) {
      case Indicators.BILLING:
        return t('common.titles.billing');
      case Indicators.TRANSACTIONS:
        return t('common.titles.transactions');
      case Indicators.PROFILE:
      default:
        return t('common.titles.settings');
    }
  };

  const noActiveSubscription = me?.subscriptions?.[0]?.stripe_customer_id && !me?.subscriptions?.[0]?.subscription_id;

  if (isLoading) return <Loading />;

  return (
    <>
      <Helmet>
        <title>{getPageTitle()}</title>
      </Helmet>

      <SettingsProvider>
        <SettingsContent>
          <SettingsHeader>{getPageTitle()}</SettingsHeader>
          <ToggleTabs />
          {noActiveSubscription && (
            <Box mt={2} mb={1} textAlign="center">
              <Alert severity="warning" variant="filled">
                <Typography variant="subtitle1">{t('billing.noActiveSubscription')}</Typography>
              </Alert>
            </Box>
          )}
          <ActiveTab />
        </SettingsContent>
      </SettingsProvider>
    </>
  );
};

export default memo(Settings);
